<script setup>
import { computed } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';

import ConversationListMenu from './widgets/conversation/ConversationListMenu.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  canManageActiveFolder: { type: Boolean, default: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  nameSearch: { type: String, default: '' },
  selectedLabels: { type: Array, default: () => [] },
  showLabelFilter: { type: Boolean, default: true },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
  'update:nameSearch',
  'update:selectedLabels',
  'search',
]);

const onNameSearchInput = event => {
  emit('update:nameSearch', event.target.value);
};

const onSearchSubmit = () => {
  emit('search', props.nameSearch);
};

const clearNameSearch = () => {
  emit('update:nameSearch', '');
  emit('search', '');
};

const { uiSettings, updateUISettings } = useUISettings();

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};
</script>

<template>
  <div
    class="flex items-center justify-between gap-2 px-3 h-[3.25rem]"
    :class="{
      'border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <div class="flex items-center justify-center min-w-0">
      <h1
        class="text-base font-medium truncate text-n-slate-12"
        :title="pageTitle"
      >
        {{ pageTitle }}
      </h1>
      <span
        v-if="
          allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
        "
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
        :title="allCount"
      >
        {{ formattedAllCount }}
      </span>
      <span
        v-if="!hasAppliedFiltersOrActiveFolders"
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
      >
        {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}
      </span>
    </div>
    <div class="relative flex-1 min-w-0">
      <span
        class="absolute i-lucide-search size-3.5 text-n-slate-10 ltr:left-2 rtl:right-2 top-1/2 -translate-y-1/2 pointer-events-none"
      />
      <input
        :value="nameSearch"
        type="text"
        :placeholder="$t('CHAT_LIST.SEARCH_BY_NAME_PLACEHOLDER')"
        class="w-full h-7 rounded-md bg-n-slate-3 border border-transparent text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand ltr:pl-7 ltr:pr-7 rtl:pr-7 rtl:pl-7"
        @input="onNameSearchInput"
        @keyup.enter="onSearchSubmit"
      />
      <button
        v-if="nameSearch"
        v-tooltip.top-end="$t('CHAT_LIST.SEARCH_BY_NAME_CLEAR')"
        class="absolute flex items-center justify-center i-lucide-x size-3.5 text-n-slate-10 hover:text-n-slate-12 ltr:right-2 rtl:left-2 top-1/2 -translate-y-1/2"
        :aria-label="$t('CHAT_LIST.SEARCH_BY_NAME_CLEAR')"
        @click="clearNameSearch"
      />
    </div>
    <div class="flex items-center gap-1 shrink-0">
      <template v-if="hasAppliedFilters && !hasActiveFolders">
        <div class="relative">
          <NextButton
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            icon="i-lucide-save"
            slate
            xs
            faded
            @click="emit('addFolders')"
          />
          <div
            id="saveFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          icon="i-lucide-circle-x"
          ruby
          faded
          xs
          @click="emit('resetFilters')"
        />
      </template>
      <template v-if="hasActiveFolders">
        <template v-if="canManageActiveFolder">
          <div class="relative">
            <NextButton
              id="editConversationFilterButton"
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
              icon="i-lucide-pen-line"
              slate
              xs
              faded
              @click="emit('filtersModal')"
            />
            <div
              id="conversationFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            id="deleteConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
            icon="i-lucide-trash-2"
            ruby
            xs
            faded
            @click="emit('deleteFolders')"
          />
        </template>
        <NextButton
          v-else
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.READ_ONLY_TOOLTIP')"
          :aria-label="$t('FILTER.CUSTOM_VIEWS.READ_ONLY_TOOLTIP')"
          icon="i-lucide-info"
          slate
          xs
          faded
        />
      </template>
      <div class="relative">
        <ConversationListMenu
          :is-on-expanded-layout="isOnExpandedLayout"
          :selected-labels="selectedLabels"
          :show-basic-filters="!hasAppliedFiltersOrActiveFolders"
          :show-label-filter="showLabelFilter"
          @change-filter="onBasicFilterChange"
          @update:selected-labels="emit('update:selectedLabels', $event)"
          @open-filters="emit('filtersModal')"
          @toggle-layout="toggleConversationLayout"
        />
        <div
          v-if="!hasActiveFolders"
          id="conversationFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
        />
      </div>
    </div>
  </div>
</template>
